import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, Check, ChevronUp } from 'lucide-react';
import { Settings } from '@vibe/icons';
import { signerColor } from './sign-overlays';

/**
 * "N to complete" — the send checklist, in the footer beside the button it
 * explains.
 *
 * It used to be a permanent block in a right-hand panel, where it asked for
 * attention the whole time and got none. The list is only ever interesting at
 * one moment — when Send is disabled and you want to know why — so it costs
 * nothing until then and appears exactly where the question is asked.
 *
 * Grouped BY PERSON, not by rule. Three signers with nothing set up produced
 * six rows and six identical red triangles, which made a half-built request
 * look like a catastrophe and made the icon meaningless by repetition. One line
 * per signer, with one line under it per thing they are missing, turns six
 * alarms into three names — and each line stays a specific task rather than one
 * vague one.
 *
 * The count follows the groups, so it counts things to go and DO rather than
 * rules currently failing. The triangle survives on the trigger, where there is
 * exactly one of it and it still means something.
 *
 * Advisory items sit under "Note" and are never counted: a document nobody
 * signs is a legitimate exhibit, and putting it in a number beside a disabled
 * button would say it is what is stopping you.
 */
export default function SendChecklist({ errors = [], warnings = [], documentCount = 0, onOpenSettings }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // Pointerdown, not click: a click listener fires on the same event that
    // opened this and closes it again immediately.
    const away = (e) => {
      if (!wrapRef.current?.contains(e.target)) setOpen(false);
    };
    const key = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('pointerdown', away);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('pointerdown', away);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  // Nothing wrong and nothing to advise: the footer says nothing rather than
  // congratulating you on a form you have not submitted.
  if (!errors.length && !warnings.length) return null;

  const { people, board, request, count } = groupErrors(errors);

  return (
    <div className='relative' ref={wrapRef}>
      {open && (
        <div className='absolute right-0 bottom-full z-20 mb-2 w-80 p-3 bg-white border border-gray-200 rounded-lg shadow-xl'>
          <p className='mb-2 text-[10px] font-semibold uppercase tracking-widest text-gray-400'>
            Before sending
          </p>

          {!count && (
            <p className='flex items-start gap-1.5 text-xs leading-snug text-gray-600'>
              <Check size={13} className='flex-shrink-0 mt-0.5' style={{ color: '#00854d' }} />
              <span>
                {documentCount} document{documentCount === 1 ? '' : 's'} ready to go out
              </span>
            </p>
          )}

          {people.map((person) => (
            <div key={person.signerIndex} className='mb-1.5'>
              <div className='flex items-center gap-1.5 text-[11.5px] font-semibold text-gray-900'>
                <span
                  className='w-[7px] h-[7px] rounded-full flex-shrink-0'
                  style={{ background: signerColor(person.signerIndex).color }}
                />
                {person.roleLabel}
              </div>
              {/* Indented under the name, ONE LINE PER NEED: the person is the
                  heading, and each thing missing is its own item to go and tick
                  off. Joined into a sentence they read as one vague task rather
                  than two specific ones. */}
              {person.needs.map((need) => (
                <p key={need} className='ml-[13px] text-[11.5px] leading-snug text-red-700'>
                  Requires {need}
                </p>
              ))}
            </div>
          ))}

          {request.length > 0 && (
            <>
              <GroupHeading>This request</GroupHeading>
              {request.map((e, i) => (
                <p key={`r${i}`} className='mb-1.5 text-[11.5px] leading-snug text-red-700'>
                  {e.message}
                </p>
              ))}
            </>
          )}

          {board.length > 0 && (
            <>
              <GroupHeading>This board</GroupHeading>
              {board.map((e, i) => (
                <p key={`b${i}`} className='text-[11.5px] leading-snug text-red-700'>
                  {e.message}
                </p>
              ))}
              {/* Under the board items, not at the bottom of everything: a
                  settings link below a list of signer problems reads as the fix
                  for those too. */}
              {onOpenSettings && (
                <button
                  type='button'
                  onClick={() => {
                    setOpen(false);
                    onOpenSettings();
                  }}
                  className='flex items-center gap-1.5 mt-2 text-xs font-medium text-blue-600 hover:text-blue-700'
                >
                  <Settings size={13} />
                  Open board settings
                </button>
              )}
            </>
          )}

          {warnings.length > 0 && (
            <>
              <GroupHeading>Note</GroupHeading>
              {warnings.map((w, i) => (
                <p key={`w${i}`} className='mb-1 text-[11.5px] leading-snug text-gray-500'>
                  {w.message}
                </p>
              ))}
            </>
          )}
        </div>
      )}

      <button
        type='button'
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        title={count ? 'What is left before this can send' : 'Worth a look before you send'}
        className={`flex items-center gap-1.5 px-2.5 py-1 text-xs font-semibold border-l-[3px] ${
          count
            ? 'text-amber-700 bg-amber-50 border border-amber-200 border-l-amber-600'
            : 'text-gray-500 bg-gray-50 border border-gray-200 border-l-gray-300'
        }`}
      >
        <AlertTriangle size={12} />
        {count ? `${count} to complete` : `${warnings.length} to check`}
        <ChevronUp size={11} className={open ? 'rotate-180 transition-transform' : 'transition-transform'} />
      </button>
    </div>
  );
}

/**
 * Fold the flat error list into the groups the popover renders.
 *
 * A signer contributes ONE entry however many of its rules are failing, which
 * is what makes the count "things to go and do" rather than "rules currently
 * broken". Roster order is preserved so the list reads top to bottom the way
 * the rail does.
 *
 * Anything without a `signerIndex`/`need` pair and without `scope: 'board'`
 * falls through to "This request" verbatim — an unrecognised error must still
 * be shown, or a new validation rule would silently stop appearing.
 */
export function groupErrors(errors = []) {
  const byIndex = new Map();
  const board = [];
  const request = [];

  for (const e of errors) {
    if (e.scope === 'board') {
      board.push(e);
    } else if (Number.isInteger(e.signerIndex) && e.need) {
      const found = byIndex.get(e.signerIndex);
      if (found) {
        found.needs.push(e.need);
      } else {
        byIndex.set(e.signerIndex, {
          signerIndex: e.signerIndex,
          roleLabel: e.roleLabel || `Signer ${e.signerIndex + 1}`,
          needs: [e.need],
        });
      }
    } else {
      request.push(e);
    }
  }

  const people = [...byIndex.values()].sort((a, b) => a.signerIndex - b.signerIndex);
  return { people, board, request, count: people.length + board.length + request.length };
}

function GroupHeading({ children }) {
  return (
    <div className='flex items-center gap-1.5 mt-2.5 mb-1'>
      <span className='text-[10px] font-semibold uppercase tracking-wider text-gray-400'>{children}</span>
      <span className='flex-1 h-px bg-gray-100' />
    </div>
  );
}
